import React from "react";
import {
  getTotalSelectedUdfCount,
  type Item,
  type UDFCategory,
} from "../ReportServices";
import type { ReportParamsViewStyles } from "./ReportParamsViewStyles";

type ReportUdfSelectorProps = {
  styles: ReportParamsViewStyles;
  visible: boolean;
  udfs: Item[];
  udfCategories: UDFCategory[];
  selectedUdfs: string;
  areUdfsLoading: boolean;
  udfComboboxQuery: string;
  setUdfComboboxQuery: (value: string) => void;
  handleMasterUdfToggle: (isSelected: boolean) => void;
  handleUdfCategoryToggle: (categoryName: string, isSelected: boolean) => void;
  handleUdfItemToggle: (itemId: string, isSelected: boolean) => void;
};

const ReportUdfSelector: React.FC<ReportUdfSelectorProps> = ({
  styles,
  visible,
  udfs,
  udfCategories,
  selectedUdfs,
  areUdfsLoading,
  udfComboboxQuery,
  setUdfComboboxQuery,
  handleMasterUdfToggle,
  handleUdfCategoryToggle,
  handleUdfItemToggle,
}) => {
  if (!visible) return null;

  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <label className={styles.parameterLabel} style={{ display: "block", marginBottom: "8px" }}>
        User Defined
      </label>
      <div className={styles.parameterControl} style={{ padding: "8px", width: "260px", maxHeight: "108px", overflowY: "auto", border: "0.25pt solid #C4E1E0", borderRadius: "6px" }}>
        {areUdfsLoading && <div style={{ color: "#666" }}>Loading...</div>}
        {!areUdfsLoading && (udfs?.length ?? 0) === 0 && <div style={{ color: "#666" }}>No items available</div>}
        {!areUdfsLoading && (udfs?.length ?? 0) > 0 && (
          <>
            {(() => {
              const { selected, total } = getTotalSelectedUdfCount(selectedUdfs, udfCategories);
              const allSelected = selected === total && total > 0;
              const someSelected = selected > 0 && selected < total;

              return (
                <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #E0E0E0" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#001C71" }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={(e) => handleMasterUdfToggle(e.target.checked)}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    <span>Select All</span>
                    <span style={{ marginLeft: "auto", color: "#666", fontSize: "0.85em" }}>
                      {selected}/{total}
                    </span>
                  </label>
                </div>
              );
            })()}
            {udfCategories.map((category) => {
              const selectedIds = new Set(selectedUdfs ? selectedUdfs.split(", ") : []);
              const categoryItemsSelected = category.items.filter((item) => selectedIds.has(item.id)).length;
              const allCategoryItemsSelected = categoryItemsSelected === category.items.length;
              const someSelected = categoryItemsSelected > 0 && categoryItemsSelected < category.items.length;

              const filteredItems = category.items.filter(
                (item) => udfComboboxQuery === "" || item.name.toLowerCase().includes(udfComboboxQuery)
              );

              if (udfComboboxQuery !== "" && filteredItems.length === 0) {
                return null;
              }

              return (
                <div key={category.displayName} style={{ marginBottom: "12px" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: "4px", fontWeight: 500, color: "#001C71" }}>
                    <input
                      type="checkbox"
                      checked={allCategoryItemsSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={(e) => handleUdfCategoryToggle(category.displayName, e.target.checked)}
                      style={{ marginRight: "8px", cursor: "pointer" }}
                    />
                    <span>{category.displayName}</span>
                    <span style={{ marginLeft: "auto", color: "#666", fontSize: "0.85em" }}>
                      {categoryItemsSelected}/{category.items.length}
                    </span>
                  </label>
                  <div style={{ marginLeft: "24px", marginTop: "4px" }}>
                    {filteredItems.map((item) => {
                      const isSelected = selectedIds.has(item.id);
                      return (
                        <label key={item.id} style={{ display: "flex", alignItems: "center", cursor: "pointer", marginBottom: "4px", fontSize: "0.95em" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleUdfItemToggle(item.id, e.target.checked)}
                            style={{ marginRight: "8px", cursor: "pointer" }}
                          />
                          <span>{item.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
        {!areUdfsLoading && (udfs?.length ?? 0) > 0 && (
          <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #E0E0E0" }}>
            <input
              type="text"
              placeholder="Filter items..."
              value={udfComboboxQuery}
              onChange={(e) => setUdfComboboxQuery(e.target.value.toLowerCase())}
              style={{
                width: "100%",
                padding: "6px",
                border: "0.25pt solid #C4E1E0",
                borderRadius: "4px",
                fontSize: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportUdfSelector;
